import { z } from 'zod';
import { cardNumberSchema, cardReferenceSchema } from './board';
import { cardCoverSchema } from './card-cover';
import { cardDetailSchema } from './card-detail';
import { dueReminderOffsetSchema } from './card-dates';
import { cardLocationSchema } from './card-location';
import { cardCustomFieldSchema } from './custom-field';
import { ulidSchema } from './id';
import { labelColourSchema } from './label';
import { placedStickerSchema } from './sticker';
import { publicProfileSchema } from './user';

const timestampViewSchema = z.string().min(1);

export const cardMergeSourceCardSchema = z.strictObject({
  id: ulidSchema,
  boardId: ulidSchema,
  listId: ulidSchema,
  title: z.string().min(1),
});

export type CardMergeSourceCard = z.infer<typeof cardMergeSourceCardSchema>;

export const cardMergeSourcePositionSchema = z.strictObject({
  cardId: ulidSchema,
  listId: ulidSchema,
  position: z.string().min(1),
  archived: z.boolean(),
  archivedAt: timestampViewSchema.nullable(),
});

export type CardMergeSourcePosition = z.infer<typeof cardMergeSourcePositionSchema>;

export const cardMergeBodySchema = z.strictObject({
  sourceCardId: ulidSchema,
});

export type CardMergeBody = z.infer<typeof cardMergeBodySchema>;

export const cardMergeResultSchema = z.strictObject({
  card: cardDetailSchema,
  sourceCards: z.array(cardMergeSourceCardSchema).length(2),
  sourcePositions: z.array(cardMergeSourcePositionSchema).length(2),
  activityEntryId: ulidSchema,
});

export type CardMergeResult = z.infer<typeof cardMergeResultSchema>;

export const cardShareListRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().min(1),
  archived: z.boolean(),
});

export type CardShareListRef = z.infer<typeof cardShareListRefSchema>;

export const cardShareCardSchema = z.strictObject({
  id: ulidSchema,
  boardId: ulidSchema,
  listId: ulidSchema,
  title: z.string().min(1),
  description: z.string().nullable(),
  position: z.string().min(1),
  shortLink: z.string().min(1),
  cardNumber: cardNumberSchema,
  /** `KEY-NNNN`, or `null` exactly when `path` carries the short link — `cardReferenceSchema` in `./board`. */
  ref: cardReferenceSchema.nullable(),
  path: z.string().min(1),
  archived: z.boolean(),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
  list: cardShareListRefSchema,
  startAt: timestampViewSchema.nullable(),
  dueAt: timestampViewSchema.nullable(),
  /** `cards.due_all_day` — FR-EXP-029 edge case (BE-44.7): an export without it re-imports an all-day due as a shifting instant. */
  dueAllDay: z.boolean(),
  dueReminderMinutesBefore: dueReminderOffsetSchema.nullable(),
  completed: z.boolean(),
  cover: cardCoverSchema.nullable(),
  location: cardLocationSchema.nullable(),
});

export type CardShareCard = z.infer<typeof cardShareCardSchema>;

export const cardShareMemberSchema = publicProfileSchema.extend({
  joinedAt: timestampViewSchema,
});

export type CardShareMember = z.infer<typeof cardShareMemberSchema>;

export const cardShareLabelSchema = z.strictObject({
  id: ulidSchema,
  name: z.string().nullable(),
  colour: labelColourSchema,
});

export type CardShareLabel = z.infer<typeof cardShareLabelSchema>;

export const cardShareChecklistItemSchema = z.strictObject({
  id: ulidSchema,
  text: z.string().min(1),
  checked: z.boolean(),
  position: z.string().min(1),
  dueAt: timestampViewSchema.nullable(),
  assigneeUserId: ulidSchema.nullable(),
  convertedToCardId: ulidSchema.nullable(),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
});

export type CardShareChecklistItem = z.infer<typeof cardShareChecklistItemSchema>;

export const cardShareChecklistSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().min(1),
  position: z.string().min(1),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
  items: z.array(cardShareChecklistItemSchema),
});

export type CardShareChecklist = z.infer<typeof cardShareChecklistSchema>;

export const cardShareAttachmentSchema = z.strictObject({
  id: ulidSchema,
  kind: z.enum(['file', 'link']),
  displayName: z.string().min(1),
  sizeBytes: z.number().int().nonnegative().nullable(),
  cloudProvider: z.string().nullable(),
  uploadedBy: publicProfileSchema.nullable(),
  createdAt: timestampViewSchema,
  updatedAt: timestampViewSchema,
});

export type CardShareAttachment = z.infer<typeof cardShareAttachmentSchema>;

export const cardShareCommentSchema = z.strictObject({
  id: ulidSchema,
  body: z.string().min(1),
  author: publicProfileSchema.nullable(),
  createdAt: timestampViewSchema,
  editedAt: timestampViewSchema.nullable(),
});

export type CardShareComment = z.infer<typeof cardShareCommentSchema>;

export const cardShareCardRefSchema = z.strictObject({
  id: ulidSchema,
  title: z.string().min(1),
  archived: z.boolean(),
});

export type CardShareCardRef = z.infer<typeof cardShareCardRefSchema>;

export const cardShareMergeLineageSchema = z.strictObject({
  id: ulidSchema,
  mergedCard: cardShareCardRefSchema,
  sourceCard: cardShareCardRefSchema,
  mergedBy: publicProfileSchema.nullable(),
  mergedAt: timestampViewSchema,
});

export type CardShareMergeLineage = z.infer<typeof cardShareMergeLineageSchema>;

export const cardExportSchema = z.strictObject({
  card: cardShareCardSchema,
  canonicalUrl: z.string().url(),
  labels: z.array(cardShareLabelSchema),
  members: z.array(cardShareMemberSchema),
  checklists: z.array(cardShareChecklistSchema),
  attachments: z.array(cardShareAttachmentSchema),
  comments: z.array(cardShareCommentSchema),
  stickers: z.array(placedStickerSchema),
  customFields: z.array(cardCustomFieldSchema),
  mergeLineage: z.array(cardShareMergeLineageSchema),
});

export type CardExport = z.infer<typeof cardExportSchema>;

export const cardEmbedSchema = z.strictObject({
  card: cardShareCardSchema.pick({
    id: true,
    boardId: true,
    listId: true,
    title: true,
    description: true,
    path: true,
    archived: true,
    list: true,
    startAt: true,
    dueAt: true,
    dueAllDay: true,
    completed: true,
    cover: true,
    location: true,
  }),
  canonicalUrl: z.string().url(),
  labels: z.array(cardShareLabelSchema),
  members: z.array(cardShareMemberSchema),
  checklists: z.array(cardShareChecklistSchema),
  attachments: z.array(cardShareAttachmentSchema),
});

export type CardEmbed = z.infer<typeof cardEmbedSchema>;
